import type { NewsFeed, NewsItem } from '../types/news';

// GNews article type
interface GNewsApiResponse {
  title: string;
  description: string;
  content: string;
  url: string;
  image: string;
  publishedAt: string;
  source: {
    name: string;
    url: string;
  };
}

const parseArticles = (articles: GNewsApiResponse[], category: string): NewsFeed => {
  const newsItems: NewsItem[] = articles.map(
    (article: GNewsApiResponse, index: number) => ({
      id: `${encodeURIComponent(article.url)}-${index}`,
      title: article.title,
      content: article.content || article.description,
      summary: article.description,
      url: article.url,
      image: article.image,
      publishedDate: article.publishedAt,
      source: article.source?.name || 'Unknown',
    })
  );

  return {
    status: 'ok',
    feed: {
      title: `${category.charAt(0).toUpperCase() + category.slice(1)} News`,
      description: `Latest ${category} news from GNews`,
      url: '',
    },
    items: newsItems,
  };
};

export const fetchGNewsCategory = async (
  category: string,
  page: number = 1
): Promise<NewsFeed> => {
  let url: string;

  if (import.meta.env.DEV) {
    // Local dev: call GNews directly (localhost is CORS-whitelisted by GNews)
    const API_KEY = import.meta.env.VITE_GNEWS_API_KEY;
    if (!API_KEY || API_KEY.includes('your-api-key')) {
      throw new Error('GNews API key not configured');
    }
    url = `https://gnews.io/api/v4/top-headlines?country=us&lang=en&category=${category}&max=10&page=${page}&apikey=${API_KEY}`;
  } else {
    // Production: use serverless proxy — no CORS, API key stays server-side
    url = `/api/news?category=${category}&page=${page}`;
  }

  const response = await fetch(url, { headers: { Accept: 'application/json' } });

  if (!response.ok) {
    throw new Error(`GNews API error: ${response.status}`);
  }

  const data = await response.json();

  if (!data.articles) {
    throw new Error('GNews API error: No articles found');
  }

  return parseArticles(data.articles as GNewsApiResponse[], category);
};
